import { inject, Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { BehaviorSubject, catchError, forkJoin, map, of, tap } from 'rxjs';
import {
  CampaignDetails,
  EngagementMessage,
  MessagesReport,
  MessagesReportGraphs,
  RecipientReport,
  RecipientReportGraphs,
  TransformedRecipientReportData,
} from '../../pages/organization/campaigns/types/campaignDetails';
import { UserProfileService } from '../user/user-profile.service';
// import { HttpResponse } from '@angular/common/http';
import { IPagination } from '../../types/contactModule';

export interface CampaignList {
  id: string;
  campaignName: string | null;
  status: 'active' | 'completed' | 'disabled';
  created: string;
}

export interface CampaignType {
  id: number;
  name: string;
  description: string;
  createdAt: string;
  startDate: string | null;
  endDate: string | null;
}

export interface CampaignReportType {
  messagesReport: MessagesReport[];
  recipientsReport: TransformedRecipientReportData[];
  messagesGraphs: MessagesReportGraphs | null;
  recipientsGraphs: RecipientReportGraphs | null;
}

@Injectable({
  providedIn: 'root',
})
export class CampaignsService {
  apiService = inject(ApiService);
  userService = inject(UserProfileService);
  campaigns = new BehaviorSubject<CampaignType[]>([]);
  campaignDetails = new BehaviorSubject({});
  campaignStats = new BehaviorSubject({});
  campaignReports = new BehaviorSubject<CampaignReportType>({
    messagesReport: [],
    recipientsReport: [],
    messagesGraphs: null,
    recipientsGraphs: null,
  });
  isFetching = false; // used for GET requests
  isLoading = false; //used for POST,PUT etc requests;
  errorMsg: string = '';

  // timeout ID for cleanup
  private errorTimeoutId?: ReturnType<typeof setTimeout>;
  campaignUrl: string =
    'blackkonnectcxpengagement/api/engagement-campaigns/organisations/';
  campaignUrlV1: string =
    'blackkonnectcxpengagement/api/engagement-campaigns/v1/organisations/';
  campaignUuid: string = ''; // used to store the uuid of the campaign being viewed. It is initialized in the campaign-details.component.ts file.

  constructor() {}

  get organizationId() {
    return this.userService.organizationId;
  }

  // pagination related to fetched campaign
  private campaignsPagination = new BehaviorSubject<IPagination>({
    totalElements: 0,
    pageSize: 10,
    totalPages: 1,
    pageNumber: 0,
    offSet: 0,
  });

  // saving a campaign
  saveCampaign(data: any) {
    this.isLoading = true;
    return this.apiService
      .post(this.campaignUrlV1 + this.organizationId, data)
      .pipe(
        tap({
          // The list pages refetch their own page; no legacy full-list reload here.
          error: (error) => {
            this.isLoading = false;
            this.errorMsg = error.message;
            this.updateError(error.message);
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // getting campaigns
  fetchCampaigns() {
    this.isFetching = true;
    this.apiService
      .get<CampaignType[]>(this.campaignUrl + this.organizationId)
      .subscribe({
        next: (data) => {
          this.campaigns.next(data);
        },
        error: (error) => {
          this.isFetching = false;
          this.errorMsg = error.message;
          this.updateError(error.message);
        },
        complete: () => {
          this.isFetching = false;
        },
      });
  }

  // fetch campaigns with pagination and sorting and searching parameters
  fetchCampaignsAlt(params?: Record<string, any>) {
    this.isFetching = true;
    const paramLength = Object.keys(params || {}).length;
    this.apiService
      .get(
        `blackkonnectcxpengagement/api/engagement-campaigns/v2/organisations/${this.organizationId}`,
        paramLength
          ? params
          : {
              size: this.campaignsPagination.value.pageSize,
              page: this.campaignsPagination.value.pageNumber,
            }
      ) // Pass query parameters here)
      .subscribe({
        next: (data: any) => {
          // console.log('campaigns alt data :>> ', data);
          this.campaigns.next(data?.items as any);
          this.campaignsPagination.next(data?.itemPagination as IPagination);
        },
        error: (error) => {
          this.isFetching = false;
          this.errorMsg = error.message;
          this.updateError(error.message);
        },
        complete: () => {
          this.isFetching = false;
        },
      });
  }

  // return campaign as observable to subscribe to...
  get getCampaigns() {
    return this.campaigns.asObservable();
  }

  // fetching an individual campaign details
  fetchCampaignDetails(id: number, showFetching: boolean = true) {
    this.isFetching = showFetching;
    this.apiService
      .get<CampaignDetails>(this.campaignUrl + this.organizationId + '/' + id)
      .subscribe({
        next: (campaign) => {
          const messageIds = campaign.engagementMessages.map(
            (msg: any) => msg.id
          );
          // Fetch engagement messages details
          this.fetchMessagesDetails(messageIds).subscribe((messages: any[]) => {
            // Replace engagementMessageSchedules in the campaign details
            this.updateMessageSchedules(campaign, messages);

            // Emit the updated campaign details
            this.campaignDetails.next(campaign);
          });

          // Fetch campaign recipients
          this.fetchCampaignRecipients().subscribe({
            next: (recipients) => {
              // Update campaign details with recipients
              this.updateRecipients(campaign, recipients);
              // Emit the updated campaign details
              this.campaignDetails.next(campaign);
            },
          });

          // fetch campaign stats
          this.fetchCampaignStats(this.campaignUuid);
        },
        error: (error) => {
          this.isFetching = false;
          this.errorMsg = error.message;
          this.updateError(error.message);
        },
        complete: () => {
          this.isFetching = false;
        },
      });
  }

  // return campaign as observable to subscribe to...
  get getCampaignDetails() {
    return this.campaignDetails.asObservable();
  }

  // Function to fetch campaign receipients
  fetchCampaignRecipients() {
    return this.apiService
      .get(
        this.campaignUrlV1 +
          this.organizationId +
          '/campaign/' +
          this.campaignUuid +
          '/receipients'
      )
      .pipe(
        tap({
          error: (error) => {
            this.isFetching = false;
            this.errorMsg = error.message;
            this.updateError(error.message);
          },
        })
      );
  }

  private fetchMessagesDetails(messageIds: number[]) {
    const requests = messageIds.map((id) =>
      this.apiService
        .get<EngagementMessage>(
          'blackkonnectcxpengagement/api/engagement-messages/' + id
        )
        .pipe(
          catchError((error) => {
            return of({ id, error: true }); // Return an object indicating the error for this ID
          })
        )
    );

    return forkJoin(requests);
  }

  // function to update main campaign details with message schedules
  private updateMessageSchedules(campaign: any, messages: any[]) {
    campaign.engagementMessages = campaign.engagementMessages.map(
      (message: any) => {
        const fetchedMessage = messages.find((msg) => msg.id === message.id);

        if (fetchedMessage) {
          if (fetchedMessage.error) {
            return { ...message, error: true }; // Add an error flag to the message if fetching failed
          }
          message.engagementMessageSchedules =
            fetchedMessage.engagementMessageSchedules;
        }

        return message;
      }
    );
  }

  // function to update recipients in campaign details
  private updateRecipients(campaign: CampaignDetails, recipients: any) {
    campaign.engagementOrganizations =
      recipients.engagementRecipientCompanies.map((org: any) => ({
        // id: org.id,
        organizationId: org.uuid,
        // organizationIntId: org.organizationIntId,
        name: org.name,
      }));
    campaign.engagementRecipientTags = recipients.engagementRecipientTags.map(
      (tag: any) => ({
        // id: tag.id,
        tagUuid: tag.uuid,
        // tagIntId: tag.tagIntId,
        name: tag.name,
      })
    );
    campaign.engagementRecipientSegments =
      recipients.engagementRecipientSegments.map((segment: any) => ({
        // id: segment.id,
        segmentUuid: segment.uuid,
        // segmentIntId: segment.segmentIntId,
        name: segment.name,
      }));
  }

  // fetch campaign statistics
  fetchCampaignStats(uuid: string, showFetching: boolean = true) {
    this.isFetching = showFetching;
    this.apiService
      .get<CampaignDetails>(
        this.campaignUrlV1 +
          this.organizationId +
          '/campaign/' +
          uuid +
          '/details/stat'
      )
      .subscribe({
        next: (data) => {
          this.campaignStats.next(data);
        },
        error: (error) => {
          this.isFetching = false;
          this.errorMsg = error.message;
          this.updateError(error.message);
        },
        complete: () => {
          this.isFetching = false;
        },
      });
  }

  // return campaign stats as observable to subscribe to...
  get getCampaignStats() {
    return this.campaignStats.asObservable();
  }

  // fetch campaign reports for both messages and recipients
  fetchCampaignReport(id?: string) {
    this.isFetching = true;

    forkJoin({
      messagesReport: this.apiService.get<any[]>(
        'blackkonnectcxpengagement/api/v1/engagement-campaign-message-reports/organization/' +
          this.organizationId +
          '/campaign/' +
          id
      ),
      recipientsReport: this.apiService
        .get<RecipientReport[]>(
          'blackkonnectcxpengagement/api/v1/engagement-campaign-recipient-reports/organization/' +
            this.organizationId +
            '/campaign/' +
            id
        )
        .pipe(
          map(
            (
              recipients: RecipientReport[]
            ): TransformedRecipientReportData[] => {
              // Initial empty array for transformed data
              const transformedData: Array<{
                groupName: string;
                groupList: RecipientReport[];
              }> = [];

              // Check and process ORGANIZATION or COMPANY group types
              const organizations = recipients.filter(
                (item) =>
                  item.groupType === 'ORGANIZATION' ||
                  item.groupType === 'COMPANY'
              );
              if (organizations.length > 0) {
                transformedData.push({
                  groupName: 'organization',
                  groupList: organizations,
                });
              }

              // Check and process TAG group types
              const tags = recipients.filter(
                (item) => item.groupType === 'TAG'
              );
              if (tags.length > 0) {
                transformedData.push({ groupName: 'tags', groupList: tags });
              }

              // Check and process SEGMENT group types
              const segments = recipients.filter(
                (item) => item.groupType === 'SEGMENT'
              );
              if (segments.length > 0) {
                transformedData.push({
                  groupName: 'segments',
                  groupList: segments,
                });
              }

              return transformedData;
            }
          )
        ),
      messagesGraphs: this.apiService.get<MessagesReportGraphs>(
        'blackkonnectcxpengagement/api/v1/engagement-campaign-message-reports/organization/' +
          this.organizationId +
          '/campaign/' +
          this.campaignUuid +
          '/messages/charts'
      ),
      recipientsGraphs: this.apiService.get<RecipientReportGraphs>(
        'blackkonnectcxpengagement/api/v1/engagement-campaign-recipient-reports/organization/' +
          this.organizationId +
          '/campaign/' +
          this.campaignUuid +
          '/recipient/charts'
      ),
    }).subscribe({
      next: (data) => {
        this.campaignReports.next(data);
      },
      error: (error) => {
        this.isFetching = false;
        this.errorMsg = error.message;
        this.updateError(error.message);
      },
      complete: () => {
        this.isFetching = false;
      },
    });
  }

  get getCampaignReport() {
    return this.campaignReports.asObservable();
  }

  // edit a campaign
  editCampaign(id: number, data: any, showFetching?: boolean) {
    this.isLoading = true;
    return this.apiService
      .put(this.campaignUrl + this.organizationId + '/' + id, data)
      .pipe(
        tap({
          next: () => {
            // Contact created successfully, now fetch the contacts
            this.fetchCampaignDetails(id, showFetching);
          },
          error: (error) => {
            this.isLoading = false;
            this.errorMsg = error.message;
            this.updateError(error.message);
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // deleting a campaign
  deleteCampaign(id: number) {
    this.isLoading = true;
    return this.apiService
      .delete(this.campaignUrl + this.organizationId + '/' + id)
      .pipe(
        tap({
          // The list pages refetch their own page; no legacy full-list reload here.
          error: (error) => {
            this.isLoading = false;
            this.errorMsg = error.message;
            this.updateError(error.message);
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // fetchCampaignMessageSchedules
  fetchMessageSchedules(id: number) {
    return this.apiService
      .get<EngagementMessage>(
        'blackkonnectcxpengagement/api/engagement-messages/' + id
      )
      .pipe(
        tap({
          error: (error) => {
            this.isLoading = false;
            this.errorMsg = error.message;
            this.updateError(error.message);
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // function to update error message.
  private updateError(msg: string) {
    if (msg) {
      // Clear existing timeout before setting a new one
      if (this.errorTimeoutId) {
        clearTimeout(this.errorTimeoutId);
      }
      this.errorMsg = msg;
      this.errorTimeoutId = setTimeout(() => {
        this.errorMsg = '';
        this.errorTimeoutId = undefined;
      }, 5000);
    }
  }

  get getCampaignsPagination() {
    return this.campaignsPagination.asObservable();
  }
}
